import { type FC, useMemo } from 'react'
import { useIntl } from 'react-intl'

import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'

import { TOTAL_CHART_MINUTES } from '@/pages/stats/chartUtils'
import { type ChartDataPoint, NOW_DATE, type SelectedDate } from '@/pages/stats/useStatsPage'

export interface StatsChartProps {
  selectedDates: SelectedDate[]
  chartData: ChartDataPoint[]
  height?: number
  showLegend?: boolean
  showYAxisLabel?: boolean
  compact?: boolean
}

// Time constants
const START_HOUR = 17

// Format timeMinutes to time string for axis labels
const formatTimeMinutes = (timeMinutes: number): string => {
  const totalMinutes = START_HOUR * 60 + timeMinutes
  const hour = Math.floor(totalMinutes / 60) % 24
  const minute = totalMinutes % 60
  return `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`
}

export const StatsChart: FC<StatsChartProps> = ({
  selectedDates,
  chartData,
  height = 600,
  showLegend = true,
  showYAxisLabel = true,
  compact = false,
}) => {
  const intl = useIntl()

  // Derive current time from chartData instead of maintaining separate state.
  // The "now" point is inserted by buildChartData with time='' when in live mode.
  const currentTimeMinutes = useMemo(() => {
    // Only show reference line if we have a "now" date selected (live mode)
    const hasNowDate = selectedDates.some((d) => d.date === NOW_DATE)
    if (!hasNowDate) return null

    // Find the "now" point inserted by buildChartData (it has time='')
    const nowPoint = chartData.find((point) => point.time === '')
    return nowPoint?.timeMinutes ?? null
  }, [selectedDates, chartData])

  // Check if projection data exists in chartData
  const hasProjection = useMemo(() => chartData.some((point) => point.projection != null), [chartData])

  // Compact mode uses fewer ticks and smaller margins
  const xAxisTicks = compact
    ? [0, 120, 240, 360, 480, 600, TOTAL_CHART_MINUTES] // Every 2 hours for compact (17:00 to 04:00)
    : [0, 60, 120, 180, 240, 300, 360, 420, 480, 540, 600, TOTAL_CHART_MINUTES] // Every hour for full

  return (
    <ResponsiveContainer width="100%" height={height}>
      <LineChart
        data={chartData}
        margin={
          compact
            ? { top: 5, right: 10, left: 0, bottom: 5 }
            : { top: 20, right: 30, left: 20, bottom: showLegend ? 60 : 40 }
        }>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis
          dataKey="timeMinutes"
          type="number"
          domain={[0, TOTAL_CHART_MINUTES]}
          tickFormatter={formatTimeMinutes}
          tick={{ fontSize: compact ? 10 : 12 }}
          angle={compact ? 0 : -45}
          textAnchor={compact ? 'middle' : 'end'}
          height={compact ? 20 : 60}
          ticks={xAxisTicks}
        />
        <YAxis
          tick={{ fontSize: compact ? 10 : 12 }}
          width={compact ? 30 : undefined}
          label={
            showYAxisLabel
              ? {
                  value: intl.formatMessage({ id: 'stats.yAxisLabel' }),
                  angle: -90,
                  position: 'insideLeft',
                  style: { textAnchor: 'middle', fontSize: 14 },
                }
              : undefined
          }
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
          }}
          labelFormatter={(timeMinutes: number) => formatTimeMinutes(timeMinutes)}
          formatter={(value: number, name: string) => {
            if (name === 'projection') {
              return [value, intl.formatMessage({ id: 'stats.projection' })]
            }
            const date = selectedDates.find((d) => `date_${d.date}` === name)
            return [value, date?.label ?? name]
          }}
        />
        {showLegend && (
          <Legend
            formatter={(value: string) => {
              if (value === 'projection') {
                return intl.formatMessage({ id: 'stats.projection' })
              }
              const date = selectedDates.find((d) => `date_${d.date}` === value)
              return date?.label ?? value
            }}
            wrapperStyle={{ paddingTop: 20 }}
          />
        )}
        {currentTimeMinutes != null && (
          <ReferenceLine
            x={currentTimeMinutes}
            stroke="#9ca3af"
            strokeWidth={2}
            strokeDasharray="4 4"
            label={{
              value: intl.formatMessage({ id: 'stats.now' }),
              position: 'top',
              fill: '#6b7280',
              fontSize: 12,
            }}
          />
        )}
        {selectedDates.map((date) => (
          <Line
            key={date.date}
            type="monotone"
            dataKey={`date_${date.date}`}
            name={`date_${date.date}`}
            stroke={date.color}
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 4 }}
          />
        ))}
        {hasProjection && (
          <Line
            type="monotone"
            dataKey="projection"
            name="projection"
            stroke="#94a3b8"
            strokeWidth={2}
            strokeDasharray="5 5"
            dot={false}
            activeDot={{ r: 4 }}
          />
        )}
      </LineChart>
    </ResponsiveContainer>
  )
}
